import { useState } from 'react';
import type { FC } from 'react';

// ─── BranchPicker ───────────────────────────────────────────────────────────
// Lista de sucursales reutilizable por todas las superficies del switch
// (dropdown del sidebar, flyout colapsado, sheet móvil, drawer móvil).
// Para comercios con muchas sucursales (probado con 18):
//   • Buscador sticky que filtra por nombre (case-insensitive).
//   • Contador "N de M" / "N sucursales".
//   • Lista con altura tope + scroll PROPIO + overscroll-behavior:contain, así
//     el scroll NO se propaga al menú detrás (fix de la regresión de tablet).
// variant: 'light' (sidebar/sheet sobre blanco) | 'dark' (drawer sobre primary).

type BranchLike = { nombre: string; id_comercio?: string };

type BranchPickerProps = {
  branches: BranchLike[];
  current: string;
  onPick: (nombre: string) => void;
  variant?: 'light' | 'dark';
  autoFocus?: boolean;
  maxHeight?: number | string;
};

const CheckIcon = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color }}>
    <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BranchPicker: FC<BranchPickerProps> = ({
  branches,
  current,
  onPick,
  variant = 'light',
  autoFocus = false,
  maxHeight = 240,
}) => {
  const [q, setQ] = useState('');
  const dark = variant === 'dark';
  const query = q.trim().toLowerCase();
  const filtered = query ? branches.filter((b) => b.nombre.toLowerCase().includes(query)) : branches;

  const txt = dark ? '#ffffff' : '#253746';
  const sub = dark ? 'rgba(255,255,255,.7)' : '#6B6B6B';
  const fieldBg = dark ? 'rgba(255,255,255,.1)' : '#EDF5F7';
  const fieldBorder = dark ? 'rgba(255,255,255,.25)' : '#E5ECEE';
  const selBg = dark ? 'rgba(255,255,255,.15)' : '#EDF5F7';
  const hoverBg = dark ? 'rgba(255,255,255,.08)' : '#F2F6F8';

  return (
    <div>
      <div style={{ position: 'relative', marginBottom: 6 }}>
        <span
          style={{
            position: 'absolute',
            left: 9,
            top: '50%',
            transform: 'translateY(-50%)',
            color: sub,
            display: 'inline-flex',
            pointerEvents: 'none',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus={autoFocus}
          placeholder="Buscar sucursal…"
          aria-label="Buscar sucursal"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 10px 8px 30px',
            borderRadius: 7,
            border: `1px solid ${fieldBorder}`,
            background: fieldBg,
            color: txt,
            fontFamily: 'inherit',
            fontSize: 13,
            outline: 'none',
          }}
        />
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: sub,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          padding: '0 2px 4px',
        }}
      >
        {query ? `${filtered.length} de ${branches.length}` : `${branches.length} sucursales`}
      </div>
      <div
        style={{
          maxHeight,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          margin: '0 -2px',
        }}
      >
        {filtered.length === 0 && (
          <div style={{ padding: '14px 10px', fontSize: 13, color: sub, textAlign: 'center' }}>
            Sin resultados para “{q.trim()}”.
          </div>
        )}
        {filtered.map((b) => {
          const sel = b.nombre === current;
          return (
            <button
              key={b.id_comercio ?? b.nombre}
              onClick={() => onPick(b.nombre)}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 6,
                border: 0,
                background: sel ? selBg : 'transparent',
                color: txt,
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: sel ? 700 : 500,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!sel) e.currentTarget.style.background = hoverBg;
              }}
              onMouseLeave={(e) => {
                if (!sel) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span
                style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {b.nombre}
              </span>
              {sel && <CheckIcon color={dark ? '#ffffff' : '#4298B5'} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BranchPicker;
